import {
  ContentRegistrationType,
  RecordOrigin,
  UserInfo,
  XMLGenerationStrategy,
} from '@/common/types';
import { FormData } from '@content-registration/types';
import type { Report, ReportMetadata } from '@generated/form-types/report';
import {
  createCrossrefDocument,
  appendElement,
  appendElementIf,
  serializeXml,
} from '../xml-helpers';
import {
  buildHead,
  buildContributors,
  buildTitles,
  buildPublicationDates,
  buildIsbns,
  buildAbstracts,
  buildPublisher,
  buildDoiData,
  buildCitationList,
  buildSeriesMetadata,
  buildInstitution,
} from '../builders';

/**
 * Appends the shared tail fields that are identical in both
 * `<report-paper_metadata>` and `<report-paper_series_metadata>`.
 *
 * XSD element order (shared tail):
 * publication_date, isbn, publisher, institution, publisher_item, doi_data, citation_list
 */
function buildReportCommonTail(
  container: Element,
  meta: ReportMetadata,
  removeReferences = false
): void {
  // 1. publication_date (required)
  buildPublicationDates(container, meta.publicationDates);

  // 2. isbn (optional)
  buildIsbns(container, meta.isbn);

  // 3. publisher (optional)
  if (meta.publisher) {
    buildPublisher(container, meta.publisher);
  }

  // 4. institution (optional)
  if (meta.issuingInstitution?.institution) {
    buildInstitution(
      container,
      meta.issuingInstitution.institution,
      meta.issuingInstitution.rorId
    );
  }

  // 5. publisher_item with report-number (optional)
  const reportNumber = meta.reportNumber?.trim();
  if (reportNumber != null) {
    const pubItem = appendElement(container, 'publisher_item');
    appendElement(pubItem, 'identifier', String(reportNumber), {
      id_type: 'report-number',
    });
  }

  // 6. doi_data (required)
  buildDoiData(container, meta.doi, meta.landingPageUrl);

  // 7. citation_list (optional)
  buildCitationList(container, meta.references, removeReferences);
}

export class ReportXmlRenderingStrategy implements XMLGenerationStrategy {
  async generateXML(
    _recordType: ContentRegistrationType,
    data: FormData,
    prettyPrint = false,
    userInfo?: UserInfo,
    origin?: RecordOrigin
  ): Promise<string> {
    const reportData = data as unknown as Report;
    const meta = reportData.reportMetadata;
    const removeReferences =
      origin?.resourceFieldsImported?.references === true &&
      !meta.references?.trim();

    const doc = createCrossrefDocument();
    const root = doc.documentElement;

    // <head>
    buildHead(root, userInfo);

    // <body>
    const body = appendElement(root, 'body');
    const reportPaper = appendElement(body, 'report-paper');

    if (reportData.series) {
      // <report-paper_series_metadata>
      const container = appendElement(
        reportPaper,
        'report-paper_series_metadata'
      );

      // 1. series_metadata (required)
      buildSeriesMetadata(container, reportData.series);

      // 2. contributors (optional)
      buildContributors(container, meta.contributors);

      // 3. titles (required)
      buildTitles(container, meta.reportTitle);

      // 4. jats:abstract (optional) — BEFORE edition_number in series path
      buildAbstracts(container, meta.abstracts);

      // 5. edition_number (optional)
      appendElementIf(
        container,
        'edition_number',
        meta.editionNumber?.toString()
      );

      // 6–11. common tail
      buildReportCommonTail(container, meta, removeReferences);
    } else {
      // <report-paper_metadata>
      const container = appendElement(reportPaper, 'report-paper_metadata');

      // 1. contributors (optional)
      buildContributors(container, meta.contributors);

      // 2. titles (required)
      buildTitles(container, meta.reportTitle);

      // 3. edition_number (optional) — BEFORE abstracts in no-series path
      appendElementIf(
        container,
        'edition_number',
        meta.editionNumber?.toString()
      );

      // 4. jats:abstract (optional)
      buildAbstracts(container, meta.abstracts);

      // 5–10. common tail
      buildReportCommonTail(container, meta, removeReferences);
    }

    return serializeXml(doc, prettyPrint);
  }
}
